/**
 * When a campaign sends, as the server decides it (foundation
 * apdm/campaign-window): only inside its daily window, every day from its
 * start to its end, except that a launch may open the start day early.
 */
import { ApdmCampaignSchedule } from '@euler/model/apdm';
import dayjs, { Dayjs } from 'dayjs';

type SendingSchedule = Pick<
  ApdmCampaignSchedule,
  'startAt' | 'endAt' | 'dailyWindow' | 'opensEarlyAt'
>;

function clockOn(day: Dayjs, clock: string): Dayjs {
  const [hours, minutes] = clock.split(':').map(Number);
  return day.hour(hours).minute(minutes).second(0).millisecond(0);
}

function opensAt(schedule: SendingSchedule, day: Dayjs): Dayjs {
  const opens = clockOn(day, schedule.dailyWindow.from);
  if (!schedule.opensEarlyAt) return opens;
  const early = dayjs(schedule.opensEarlyAt);
  return early.isSame(day, 'day') && early.isBefore(opens) ? early : opens;
}

/**
 * The first moment at or after `now` the campaign may send; null when no
 * window is left before its end.
 */
export function apdmNextSendingAt(
  schedule: SendingSchedule,
  now: Dayjs = dayjs(),
): Dayjs | null {
  const start = schedule.startAt ? dayjs(schedule.startAt) : now;
  const from = start.isAfter(now) ? start : now;
  const opensToday = opensAt(schedule, from);
  const next = from.isBefore(clockOn(from, schedule.dailyWindow.to))
    ? opensToday.isAfter(from)
      ? opensToday
      : from
    : opensAt(schedule, from.add(1, 'day'));
  return schedule.endAt && !next.isBefore(dayjs(schedule.endAt)) ? null : next;
}

/**
 * Whether sending immediately would change anything: the campaign starts
 * today and today's window has not opened yet.
 */
export function apdmCanSendImmediately(
  schedule: SendingSchedule,
  now: Dayjs = dayjs(),
): boolean {
  return (
    !!schedule.startAt &&
    dayjs(schedule.startAt).isSame(now, 'day') &&
    now.isBefore(clockOn(now, schedule.dailyWindow.from))
  );
}

/** "今天 09:00", "明天 09:00" or "10月21日 09:00". */
export function apdmSendingTimeText(at: Dayjs, now: Dayjs = dayjs()): string {
  const day = at.isSame(now, 'day')
    ? '今天'
    : at.isSame(now.add(1, 'day'), 'day')
    ? '明天'
    : at.format('M月D日');
  return `${day} ${at.format('HH:mm')}`;
}
